import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';

const footerLink = {
  color: 'var(--lk-text-quaternary)',
  fontSize: '0.6rem',
  textDecoration: 'none',
};

const disclosure = {
  fontSize: '0.65rem',
  lineHeight: 1.6,
  color: 'var(--lk-text-tertiary)',
  textAlign: 'center',
  maxWidth: 900,
  margin: '0 auto var(--lk-space-3)',
};

// Inline styles can't express media queries, so track the breakpoint here.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

const ComplianceFooter = () => {
  const currentYear = new Date().getFullYear();
  // Don't pitch a partner page to people already on it.
  const { pathname } = useLocation();
  const onLenderPage = pathname.startsWith('/lenders') || pathname.startsWith('/lender/');
  const onRealtorPage = pathname.startsWith('/realtors');

  // On mobile the legal text collapses to one line with an expand toggle.
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const showLegal = !isMobile || expanded;

  return (
    <footer style={{ background: 'var(--lk-gray-50)', borderTop: '1px solid var(--lk-border-subtle)', marginTop: 'auto', padding: 'var(--lk-space-6) 0' }}>
      <div style={{ maxWidth: 1024, margin: '0 auto', padding: '0 var(--lk-space-4)' }}>

        {/* Partner prompts (lenders / realtors) */}
        {!(onLenderPage && onRealtorPage) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 12, marginBottom: 'var(--lk-space-4)', flexWrap: 'wrap',
            padding: '12px 18px', borderRadius: 999,
            backgroundColor: 'white', border: '1px solid var(--lk-border-subtle)',
            width: 'fit-content', margin: '0 auto var(--lk-space-4)',
          }}>
            {!onLenderPage && (
              <>
                <span style={{ fontSize: 13, color: 'var(--lk-text-secondary)' }}>
                  Loan officer or lender?
                </span>
                <a href="/lenders" style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--lk-primary-600, #4f46e5)',
                  textDecoration: 'none',
                }}>
                  Join the marketplace →
                </a>
              </>
            )}
            {!onLenderPage && !onRealtorPage && (
              <span style={{ color: 'var(--lk-border-default)' }}>|</span>
            )}
            {!onRealtorPage && (
              <>
                <span style={{ fontSize: 13, color: 'var(--lk-text-secondary)' }}>
                  Realtor?
                </span>
                <a href="/realtors" style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--lk-primary-600, #4f46e5)',
                  textDecoration: 'none',
                }}>
                  Send buyers in ready →
                </a>
              </>
            )}
          </div>
        )}

        {/* Equal Housing & NMLS */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--lk-space-3)', marginBottom: 'var(--lk-space-3)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img
              src="/assets/equal-housing-opportunity.svg"
              alt="Equal Housing Opportunity"
              style={{ height: 32 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span style={{ fontSize: '0.7rem', color: 'var(--lk-text-tertiary)' }}>Equal Housing Opportunity</span>
          </div>
          <span style={{ color: 'var(--lk-border-default)' }}>|</span>
          <span style={{ fontSize: '0.7rem', color: 'var(--lk-text-tertiary)' }}>
            NMLS ID #1192984 |{' '}
            <a href="https://www.nmlsconsumeraccess.org" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--lk-text-tertiary)', textDecoration: 'underline' }}>
              NMLS Consumer Access
            </a>
          </span>
        </div>

        {/* Mobile: one-line summary + toggle for the full legal text */}
        {isMobile && (
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-3)' }}>
            <p style={{ ...disclosure, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              LoanKnow is not a lender and does not make credit decisions.
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '2px 8px',
                fontSize: '0.65rem', color: 'var(--lk-text-tertiary)', textDecoration: 'underline',
              }}
            >
              {expanded ? 'Hide legal disclosures ▴' : 'Show full legal disclosures ▾'}
            </button>
          </div>
        )}

        {showLegal && (
          <>
            {/* Compliance Disclosures */}
            <p style={disclosure}>
              LoanKnow is not a lender and does not make credit decisions. LoanKnow connects consumers with
              participating lenders who may offer loan products. Not all applicants will qualify for financing.
              Loan approval, terms, and rates are determined solely by the lender based on the applicant's
              creditworthiness and other factors. This is not a commitment to lend. Terms and conditions apply.
            </p>

            {/* Fair Lending */}
            <p style={disclosure}>
              We are committed to fair lending practices in accordance with the Equal Credit Opportunity Act (ECOA)
              and the Fair Housing Act. We do not discriminate on the basis of race, color, religion, national origin,
              sex, marital status, age, disability, familial status, or any other protected class.
            </p>

            <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />

            {/* Security Badges */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--lk-space-3)', flexWrap: 'wrap', marginBottom: 'var(--lk-space-3)', fontSize: '0.65rem', color: 'var(--lk-text-tertiary)' }}>
              <span>256-bit SSL Encryption</span>
              <span>•</span>
              <span>SOC 2 Type II Compliant</span>
              <span>•</span>
              <span>GLBA Compliant</span>
              <span>•</span>
              <span>CCPA Compliant</span>
            </div>

            {/* Privacy Statement */}
            <p style={disclosure}>
              Your privacy is important to us. We collect, use, and protect your personal information in accordance
              with the Gramm-Leach-Bliley Act (GLBA) and applicable state privacy laws including the California
              Consumer Privacy Act (CCPA). We do not sell your personal information to third parties. For more
              information, please review our{' '}
              <a href="/privacy-policy" style={{ color: 'var(--lk-text-tertiary)', textDecoration: 'underline' }}>Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms-of-service" style={{ color: 'var(--lk-text-tertiary)', textDecoration: 'underline' }}>Terms of Service</a>.
            </p>
          </>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />

        {/* Corporate */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-2)' }}>
          {showLegal && (
            <div style={{ fontSize: '0.6rem', lineHeight: 1.6, color: 'var(--lk-text-quaternary)' }}>
              LoanKnow™ is a wholly owned subsidiary of Skramco Holdings LLC.
            </div>
          )}
          <div style={{ fontSize: '0.6rem', lineHeight: 1.6, color: 'var(--lk-text-quaternary)', marginTop: 4 }}>
            {showLegal ? (
              <>
                © {currentYear} LoanKnow. All rights reserved. LoanKnow, the LoanKnow logo, and Owen are trademarks
                or registered trademarks of Skramco Holdings LLC. All other trademarks are the property of their
                respective owners.
              </>
            ) : (
              <>© {currentYear} LoanKnow. All rights reserved.</>
            )}
          </div>
        </div>

        {/* Footer Links */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--lk-space-3)', flexWrap: 'wrap', marginTop: 'var(--lk-space-2)' }}>
          <a href="/privacy-policy" style={footerLink}>Privacy Policy</a>
          <a href="/terms-of-service" style={footerLink}>Terms of Service</a>
          <a href="/licensing" style={footerLink}>Licensing</a>
          <a href="/accessibility" style={footerLink}>Accessibility</a>
          <a href="/contact" style={footerLink}>Contact Us</a>
        </div>

        {/* Portal Login Links */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginTop: 'var(--lk-space-2)', opacity: 0.5, transition: 'opacity 0.3s', fontSize: '0.55rem', color: 'var(--lk-text-quaternary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
        >
          <span>Portal Access:</span>
          <a href="/borrower/login" style={footerLink}>Borrower</a>
          <span>•</span>
          <a href="/lender/login" style={footerLink}>Lender</a>
          <span>•</span>
          <a href="/uw/login" style={footerLink}>Underwriter</a>
          <span>•</span>
          <a href="/partner/login" style={footerLink}>Partner</a>
          <span>•</span>
          <a href="/admin/login" style={footerLink}>Admin</a>
        </div>
      </div>
    </footer>
  );
};

export default ComplianceFooter;
